import { forwardRef, useImperativeHandle } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import {
    DeleteIconsOptions,
    DownloadIcon,
    EyeIcon,
    IconChipBell,
    IconClip,
    IconHandCard,
} from '../../../components/buttons/IconButtons';
import CustomCard from '../../../components/CustomCard';
import { CustomSwitch } from '../../../components/CustomSwitch';
import { CustomTextField } from '../../../components/CustomTextField';
import CustomUserChip from '../../../components/CustomUserChip';
import DivContainerBody from '../../../components/DivContainerBody';
import CustomPhotoPickerMultiple from '../../../components/PhotoPicker/CustomPhotoPickerMultiple';
import { fileSize } from '../../../constant/variable';
import { FILE_TYPE } from '../../../Helpers/fileType';
import { POSITION_MODEL } from '../../../Helpers/PositionModel';
import ManageApp from '../Restrictions/Functionality/ManageApp';
import NotificationChannelsTable from './Manage/subComponents/NotificationChannelsTable';
import {ADVANCE_BOOKING_LIMIT_LENGTH, ATTENDEES_LIMIT_LENGTH, BOOKING_NOTICE_LENGTH} from "./Model/Model";

/**
 * @Email : azapata@ibl.mx
 * @Author: Ferro Zapata Arnulfo
 * @jobTittle: Full Stack Developer
 *
 */
const Manage = forwardRef((props, ref) => {
    const {
        photoPickerFile,
        setPhotoPickerFile,
        item,
        emptyFields,
        handleChange,
        handleUpdate,
        disabled,
        isBookingWindowEnabled,
        isAttendeesLimitEnabled
    } = ManageApp();

    const { t } = useTranslation();

    /** Expose internal function to parent through ref */
    useImperativeHandle(ref, () => ({
        handleUpdate,
    }));

    return (
        <DivContainerBody>
            <div className='row'>
                <div className='col-12 col-lg-4'>
                    <div className={'row'}>
                        <CustomCard
                            classCard={`background-shades-50 border-radius-8px`}
                            classContent={'p-0'}
                            classes={'p-0'}
                            children={
                                <div className={'row g-3 mb-3'}>
                                    <div className={'border-bottom-shades-300 p-3 d-flex align-items-center'}>
                                        <div className={'mr-2'}>
                                            <EyeIcon width={24} height={24} />
                                        </div>
                                        <p className={'font-color-general-950 ibl-font-w-600 fs-14-to-13 '}>
                                            {t('user_preview')}
                                        </p>
                                    </div>
                                    <div className={'col-12'}>
                                        <div className='row'>
                                            <div className='pb-3'>
                                                <CustomUserChip
                                                    className='m-0 chip-primary-200-50 w-100 p-0 justify-content-start p-3'
                                                    name={
                                                        item.bookingWindowEnabled ? (
                                                            <Trans
                                                                i18nKey='reservations_open_days_range'
                                                                values={{
                                                                    advanceDays: item.advanceBookingLimit || 0,
                                                                    minimumNotice: item.minimumBookingNotice || 0,
                                                                }}
                                                                components={{ strong: <strong /> }}
                                                            />
                                                        ) : (
                                                            <Trans
                                                                i18nKey='booking_window_disabled'
                                                                components={{ strong: <strong /> }}
                                                            />
                                                        )
                                                    }
                                                    variant='large'
                                                    iconPosition={POSITION_MODEL.LEFT}
                                                    classNameIcon='icon-color-secondary-700'
                                                />
                                            </div>
                                        </div>
                                        <div className='row'>
                                            <div className='pb-3'>
                                                <CustomUserChip
                                                    className='m-0 chip-primary-200-50 w-100 p-0 justify-content-start p-3'
                                                    name={
                                                        !item.attendeesLimitsEnabled ? (
                                                            <Trans
                                                                i18nKey='attendees_disabled'
                                                                components={{ strong: <strong /> }}
                                                            />
                                                        ) : (
                                                            <Trans
                                                                i18nKey='attendees_limit_per_reservation'
                                                                values={{ value: item.attendeesLimitsValue }}
                                                                components={{ strong: <strong /> }}
                                                            />
                                                        )
                                                    }
                                                    variant='large'
                                                    iconPosition={POSITION_MODEL.LEFT}
                                                    classNameIcon='icon-color-secondary-700'
                                                />
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </div>
                    <div className={'row mt-4'}>
                        <CustomCard
                            classCard={`background-shades-50 border-radius-8px`}
                            classContent={'p-0'}
                            classes={'p-0'}
                            children={
                                <div className={'row g-3 mb-3'}>
                                    <div className={'border-bottom-shades-300 p-3 d-flex align-items-center'}>
                                        <div className={'mr-2'}>
                                            <IconClip width={24} height={24} />
                                        </div>
                                        <p className={'font-color-general-950 ibl-font-w-600 fs-14-to-13 '}>
                                            {t('policy_documents')}
                                        </p>
                                    </div>
                                    <div className={'col-12'}>
                                        <div className='row'>
                                            <CustomPhotoPickerMultiple
                                                id='fp_m_fine_document'
                                                photoPickerFile={photoPickerFile}
                                                setPhotoPickerFile={setPhotoPickerFile}
                                                accept={{ ...FILE_TYPE.FILES, size: fileSize }}
                                                showBtnDeleteItemGallery={true}
                                                showBtnCamera={false}
                                                deleteIcon={DeleteIconsOptions}
                                                downloadIcon={DownloadIcon}
                                                showBtnDownload={true}
                                                apiDelete={false}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </div>
                </div>
                <div className='col-sm-12 col-lg-8 mt-4 mt-lg-0'>
                    <>
                        <CustomCard
                            classCard={`background-shades-50 border-radius-8px mb-3`}
                            classContent={'p-0'}
                            classes={'p-0'}
                            children={
                                <>
                                    <div className={'border-bottom-shades-300 p-3 d-flex align-items-center'}>
                                        <div className={'mr-2'}>
                                            <IconHandCard width={24} height={24} />
                                        </div>
                                        <p className={'font-color-general-950 ibl-font-w-600 fs-14-to-13 '}>
                                            {t('reservation_policies')}
                                        </p>
                                    </div>
                                    <div className='row minh-144px'>
                                        <div className='col-12 col-lg-4 d-flex align-items-center mt-3'>
                                            <p className='fs-14px font-color-shades-950 ibl-font-w-600 mb-0'>
                                                {t('booking_window')}
                                            </p>
                                        </div>
                                        <div className='col-12 col-lg-8 d-flex justify-content-end align-items-center'>
                                            <CustomSwitch
                                                id='cb_m_booking_window_enabled'
                                                showBorder={false}
                                                name='bookingWindowEnabled'
                                                checked={item.bookingWindowEnabled === true}
                                                onChange={(e) =>
                                                    handleChange({
                                                        target: {
                                                            name: e.target.name,
                                                            value: item.bookingWindowEnabled,
                                                        },
                                                    })
                                                }
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className='col-12 col-lg-4 d-flex align-items-center mb-3'>
                                            <p className='font-color-shades-950 fs-12px mb-0'>
                                                {t('set_booking_timeframe')}
                                            </p>
                                        </div>
                                        <div className='col-12 col-lg-8 pt-4'>
                                            <div className='row'>
                                                <div className='col-12 col-md-6'>
                                                    <CustomTextField
                                                        id='txt_m_minimum_booking_notice'
                                                        name='minimumBookingNotice'
                                                        value={item.minimumBookingNotice}
                                                        trans='minimum_booking_notice'
                                                        onChange={handleChange}
                                                        type='number'
                                                        length={2}
                                                        emptyFields={emptyFields}
                                                        required={item.bookingWindowEnabled}
                                                        disabled={disabled || !isBookingWindowEnabled}
                                                        suffixText={t('days')}
                                                        isValid={ !(item.bookingWindowEnabled && (!item.minimumBookingNotice || Number(item.minimumBookingNotice) < BOOKING_NOTICE_LENGTH.MIN)) }
                                                        isValidText={t('it_must_be_greater_than_or_equal_to', { min: BOOKING_NOTICE_LENGTH.MIN })}
                                                    />
                                                </div>
                                                <div className='col-12 col-md-6'>
                                                    <CustomTextField
                                                        id='txt_m_advance_booking_limit'
                                                        name='advanceBookingLimit'
                                                        value={item.advanceBookingLimit}
                                                        trans='advance_booking_limit'
                                                        onChange={handleChange}
                                                        type='number'
                                                        length={2}
                                                        emptyFields={emptyFields}
                                                        required={item.bookingWindowEnabled}
                                                        disabled={disabled || !isBookingWindowEnabled}
                                                        suffixText={t('days')}
                                                       isValid={ !(item.bookingWindowEnabled && (!item.advanceBookingLimit || Number(item.advanceBookingLimit) < ADVANCE_BOOKING_LIMIT_LENGTH.MIN)) }
                                                        isValidText={t('it_must_be_greater_than_or_equal_to', { min: ADVANCE_BOOKING_LIMIT_LENGTH.MIN })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <div className='px-3'>
                                        <div className='border-bottom-shades-300' />
                                    </div>
                                    <div className='row pt-3 minh-144px'>
                                        <div className='col-12 col-lg-4 d-flex align-items-center'>
                                            <p className='fs-14px font-color-shades-950 ibl-font-w-600 mb-0'>
                                                {t('attendees_limits')}
                                            </p>
                                        </div>
                                        <div className='col-12 col-lg-8 d-flex justify-content-end align-items-center'>
                                            <CustomSwitch
                                                id='cb_m_is_attendees_limit'
                                                showBorder={false}
                                                name='attendeesLimitsEnabled'
                                                checked={item.attendeesLimitsEnabled === true}
                                                onChange={(e) =>
                                                    handleChange({
                                                        target: {
                                                            name: e.target.name,
                                                            value: item.attendeesLimitsEnabled,
                                                        },
                                                    })
                                                }
                                                disabled={disabled}
                                            />
                                        </div>
                                        <div className='col-12 col-lg-4 d-flex align-items-center pb-3'>
                                            <p className='font-color-shades-950 fs-12px mb-0'>
                                                {t('limit_attendees_per_reservation')}
                                            </p>
                                        </div>

                                        <div className='col-12 col-lg-8 pt-4'>
                                            <div className='row'>
                                                <div className='col-12 col-md-6'>
                                                    <CustomTextField
                                                        id='txt_m_max_attendees'
                                                        length={2}
                                                        name='attendeesLimitsValue'
                                                        value={item.attendeesLimitsValue}
                                                        trans='enter_number'
                                                        onChange={handleChange}
                                                        required={item.attendeesLimitsEnabled}
                                                        emptyFields={emptyFields}
                                                        type='number'
                                                        disabled={disabled || !isAttendeesLimitEnabled}
                                                        isValid={ !(item.attendeesLimitsEnabled && (!item.attendeesLimitsValue || Number(item.attendeesLimitsValue) < ATTENDEES_LIMIT_LENGTH.MIN)) }
                                                        isValidText={t('it_must_be_greater_than_or_equal_to', { min: ATTENDEES_LIMIT_LENGTH.MIN })}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </>
                            }
                        />
                    </>
                    <div className='pb-5 mt-4'>
                        <CustomCard
                            classCard={`background-shades-50 border-radius-8px`}
                            classContent={'p-0'}
                            classes={'p-0'}
                            children={
                                <div className={'row g-3 mb-3'}>
                                    <div className={'border-bottom-shades-300 p-3 d-flex align-items-center'}>
                                        <div className={'mr-2'}>
                                            <IconChipBell width={24} height={24} />
                                        </div>
                                        <p className={'font-color-general-950 ibl-font-w-600 fs-14-to-13 '}>
                                            {t('notification_channels')}
                                        </p>
                                    </div>
                                    <div className={'col-12 p-0 m-0'}>
                                        <div className='row'>
                                            <NotificationChannelsTable
                                                item={item}
                                                handleChange={handleChange}
                                                disabled={disabled}
                                            />
                                        </div>
                                    </div>
                                </div>
                            }
                        />
                    </div>
                </div>
            </div>
        </DivContainerBody>
    );
});
export default Manage;
